import { AsyncLoad } from '../../../components/asyncLoad/AsyncLoad';
import { LoadingSpinnerButton } from '../../../components/loadingSpinnerButton/LoadingSpinnerButton';
import { Message } from '../../../services/Message';
import useTranslation from '../../../hooks/useTranslation';
import { texts } from '../../../i18n/texts';
import { checkNotNull } from '../../../utils/checkNotNull';
import { SystemLandscape } from '../systemLandscape/SystemLandscape';
import { SystemLandscapeHeader } from '../systemLandscapeHeader/SystemLandscapeHeader';
import { ISystemLandscapeSectionProps } from './ISystemLandscapeSectionProps';
import { ISystem } from '../../../shared/model/ISystem';
import { SystemDummy } from '../../../model/SystemDummy';
import styles from './SystemLandscapeSection.module.scss';
import { useSystemLandscapeSectionViewModel } from './useSystemLandscapeSectionViewModel';
import { useEffect, useState } from 'react';

const SYSTEM_CLIPBOARD_STORAGE_KEY_V1 =
	'swpo-system-landscape-clipboard-v1';
const SYSTEM_CLIPBOARD_STORAGE_KEY_V2 =
	'swpo-system-landscape-clipboard-v2';

const normalizeClipboardParsedToBatches = (parsed: unknown): ISystem[][] => {
	if (!Array.isArray(parsed) || parsed.length === 0) {
		return [];
	}
	if (Array.isArray(parsed[0])) {
		return parsed as ISystem[][];
	}
	return [parsed as ISystem[]];
};

const loadClipboardBatchesFromStorage = (): ISystem[][] => {
	try {
		const serializedV2 = window.localStorage.getItem(
			SYSTEM_CLIPBOARD_STORAGE_KEY_V2
		);
		if (serializedV2) {
			return normalizeClipboardParsedToBatches(JSON.parse(serializedV2));
		}
		const serializedV1 = window.localStorage.getItem(
			SYSTEM_CLIPBOARD_STORAGE_KEY_V1
		);
		if (serializedV1) {
			const batches = normalizeClipboardParsedToBatches(
				JSON.parse(serializedV1)
			);
			if (batches.length > 0) {
				window.localStorage.setItem(
					SYSTEM_CLIPBOARD_STORAGE_KEY_V2,
					JSON.stringify(batches)
				);
			}
			window.localStorage.removeItem(SYSTEM_CLIPBOARD_STORAGE_KEY_V1);
			return batches;
		}
	} catch {
		window.localStorage.removeItem(SYSTEM_CLIPBOARD_STORAGE_KEY_V1);
		window.localStorage.removeItem(SYSTEM_CLIPBOARD_STORAGE_KEY_V2);
	}
	return [];
};

const persistClipboardBatches = (batches: ISystem[][]) => {
	window.localStorage.setItem(
		SYSTEM_CLIPBOARD_STORAGE_KEY_V2,
		JSON.stringify(batches)
	);
	window.localStorage.removeItem(SYSTEM_CLIPBOARD_STORAGE_KEY_V1);
};

export const SystemLandscapeSection: React.FC<ISystemLandscapeSectionProps> = (
	props
) => {
	const viewModel = useSystemLandscapeSectionViewModel(props);
	const [enableSystemLandscapeCopyPaste, setEnableSystemLandscapeCopyPaste] =
		useState(false);
	const [selectedSystemKeys, setSelectedSystemKeys] = useState<Set<string>>(
		new Set()
	);
	const [selectedSystemsByKey, setSelectedSystemsByKey] = useState<
		Record<string, ISystem>
	>({});
	const [clipboardBatches, setClipboardBatches] = useState<ISystem[][]>([]);
	const [pastedDraftSystemsByLandscape, setPastedDraftSystemsByLandscape] =
		useState<Record<string, ISystem[]>>({});
	const [systemsDraftCountsBySystemKey, setSystemsDraftCountsBySystemKey] =
		useState<
			Record<
				string,
				{ totalSystemsCount?: number; productionSystemsCount?: number }
			>
		>({});
	useEffect(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 500);
	}, [viewModel.systemLandscapeSection]);
	useEffect(() => {
		setClipboardBatches(loadClipboardBatchesFromStorage());
	}, []);

	const getSystemMatchKey = (system: ISystem) =>
		`${system.SYSTEM_ID}|||${system.SYSTEM_CLIENT}|||${system.INSTNUMBER}`;

	// After saving a pasted draft and reloading from backend, remove drafts that
	// have become persisted to avoid "duplicate" entries in the UI.
	useEffect(() => {
		const systemLandscapeSection = viewModel.systemLandscapeSection;
		if (!systemLandscapeSection) {
			return;
		}
		const persistedSystemKeysByLandscape = new Map<string, Set<string>>();
		systemLandscapeSection.SYSTEM_LANDSCAPE_BUNDLES.forEach((bundle) => {
			const landscapeId = bundle.SYS_LANDSCAPE.OBJECT_ID;
			persistedSystemKeysByLandscape.set(
				landscapeId,
				new Set(bundle.SYSTEMS.map(getSystemMatchKey))
			);
		});

		setPastedDraftSystemsByLandscape((previous) => {
			let changed = false;
			const next: Record<string, ISystem[]> = {};

			for (const [landscapeId, drafts] of Object.entries(previous)) {
				const persistedKeys = persistedSystemKeysByLandscape.get(landscapeId);
				if (!persistedKeys) {
					next[landscapeId] = drafts;
					continue;
				}
				const filteredDrafts = drafts.filter(
					(draft) => !persistedKeys.has(getSystemMatchKey(draft))
				);
				if (filteredDrafts.length !== drafts.length) {
					changed = true;
				}
				if (filteredDrafts.length > 0) {
					next[landscapeId] = filteredDrafts;
				}
			}

			return changed ? next : previous;
		});
	}, [viewModel.systemLandscapeSection]);

	const clipboardPasteBatch =
		clipboardBatches.length > 0
			? clipboardBatches[clipboardBatches.length - 1]
			: [];
	const clipboardPasteSystemsCount = clipboardPasteBatch.length;

	const { t } = useTranslation();
	const allSystemsInProduct =
		viewModel.systemLandscapeSection?.SYSTEM_LANDSCAPE_BUNDLES?.flatMap(
			(bundle) => bundle.SYSTEMS ?? []
		) ?? [];

	const onDraftCountsChange = (
		systemDraftKey: string,
		counts: { totalSystemsCount?: number; productionSystemsCount?: number }
	) => {
		setSystemsDraftCountsBySystemKey((previous) => ({
			...previous,
			[systemDraftKey]: {
				...previous[systemDraftKey],
				...counts,
			},
		}));
	};

	const onSystemDeletedForClipboardCleanup = (system: ISystem) => {
		const matchKey = getSystemMatchKey(system);

		// Remove matching drafts from all landscapes
		setPastedDraftSystemsByLandscape((previous) => {
			let changed = false;
			const next: Record<string, ISystem[]> = {};

			for (const [landscapeId, drafts] of Object.entries(previous)) {
				const filteredDrafts = drafts.filter(
					(draft) => getSystemMatchKey(draft) !== matchKey
				);
				if (filteredDrafts.length !== drafts.length) {
					changed = true;
				}
				if (filteredDrafts.length > 0) {
					next[landscapeId] = filteredDrafts;
				}
			}

			return changed ? next : previous;
		});

		// Also remove from clipboard history so it does not reappear on paste
		setClipboardBatches((previous) => {
			let changed = false;
			const next = previous
				.map((batch) => {
					const filtered = batch.filter(
						(item) => getSystemMatchKey(item) !== matchKey
					);
					if (filtered.length !== batch.length) {
						changed = true;
					}
					return filtered;
				})
				.filter((batch) => batch.length > 0);

			if (!changed) {
				return previous;
			}
			persistClipboardBatches(next);
			return next;
		});
	};

	const onEnableCopyPaste = (enabled: boolean) => {
		setEnableSystemLandscapeCopyPaste(enabled);
		if (!enabled) {
			setSelectedSystemKeys(new Set());
			setSelectedSystemsByKey({});
		}
	};

	const isSystemSelected = (systemSelectionKey: string) =>
		selectedSystemKeys.has(systemSelectionKey);

	const onToggleSystemSelection = (
		systemSelectionKey: string,
		selected: boolean,
		system: ISystem
	) => {
		setSelectedSystemKeys((previous) => {
			const next = new Set(previous);
			if (selected) {
				next.add(systemSelectionKey);
			} else {
				next.delete(systemSelectionKey);
			}
			return next;
		});
		setSelectedSystemsByKey((previous) => {
			const next = { ...previous };
			if (selected) {
				next[systemSelectionKey] = SystemDummy.clone(system);
			} else {
				delete next[systemSelectionKey];
			}
			return next;
		});
	};

	const onToggleAllSystemsInLandscape = (
		landscapeObjectId: string,
		selectAll: boolean,
		systems: ISystem[],
		pastedSystemsForLandscape: ISystem[]
	) => {
		const combined = [...systems, ...pastedSystemsForLandscape];
		setSelectedSystemKeys((previous) => {
			const next = new Set(previous);
			combined.forEach((system, index) => {
				const number = index + 1;
				const systemDraftKey =
					system.OBJECT_ID ||
					`draft-${landscapeObjectId}-${number}`;
				const systemSelectionKey = `${landscapeObjectId}:${
					system.OBJECT_ID || systemDraftKey
				}`;
				if (selectAll) {
					next.add(systemSelectionKey);
				} else {
					next.delete(systemSelectionKey);
				}
			});
			return next;
		});
		setSelectedSystemsByKey((previous) => {
			const next = { ...previous };
			combined.forEach((system, index) => {
				const number = index + 1;
				const systemDraftKey =
					system.OBJECT_ID ||
					`draft-${landscapeObjectId}-${number}`;
				const systemSelectionKey = `${landscapeObjectId}:${
					system.OBJECT_ID || systemDraftKey
				}`;
				if (selectAll) {
					next[systemSelectionKey] = SystemDummy.clone(system);
				} else {
					delete next[systemSelectionKey];
				}
			});
			return next;
		});
	};

	const onCopySelectedSystems = async () => {
		if (selectedSystemKeys.size === 0) {
			Message.warning(t(texts.systemLandscapeHeader.noSystemsSelectedForCopy));
			return;
		}
		const copiedSystems = Array.from(selectedSystemKeys)
			.map((selectionKey) => selectedSystemsByKey[selectionKey])
			.filter((system): system is ISystem => system !== undefined)
			.map((system) => {
				const clone = SystemDummy.clone(system);
				clone.OBJECT_ID = '';
				clone.CONFLICTS = [];
				clone.DOWNLOAD_HISTORY = undefined;
				return clone;
			});
		setClipboardBatches((previous) => {
			const next = [...previous, copiedSystems];
			persistClipboardBatches(next);
			return next;
		});
		Message.info(
			t(texts.systemLandscapeHeader.copiedSystemsToClipboard, {
				count: copiedSystems.length,
			})
		);
	};

	const onPasteClipboardSystems = async (landscapeId: string) => {
		if (clipboardPasteSystemsCount === 0) {
			Message.warning(t(texts.systemLandscapeHeader.clipboardEmpty));
			return;
		}
		const pastedDraftSystems = clipboardPasteBatch.map((clipboardSystem) => {
			const draftSystem = SystemDummy.create(clipboardSystem, false);
			draftSystem.CONFLICTS = [];
			draftSystem.DOWNLOAD_HISTORY = undefined;
			return draftSystem;
		});
		setPastedDraftSystemsByLandscape((previous) => ({
			...previous,
			// Replace any previously pasted drafts for this landscape so that
			// only the latest paste batch is shown as draft rows.
			[landscapeId]: pastedDraftSystems,
		}));
	};

	return (
		<>
			<AsyncLoad load={viewModel.loadSystemLandscapeSection}>
				{viewModel.systemLandscapeSection?.SYSTEM_LANDSCAPE_BUNDLES && (
					<>
						<div className={styles.systemLandscapeHeader}>
							<SystemLandscapeHeader
								product={viewModel.systemLandscapeSection.PRODUCT}
								licenseOptions={viewModel.systemLandscapeSection.LIC_OPTS}
								isLicenseOptionsRequired={
									viewModel.systemLandscapeSection.IS_LIC_OPTS_REQUIRED
								}
								onCollapseSystems={viewModel.onCollapseAllSystems}
								onExpandSystems={viewModel.onExpandAllSystems}
								systemsExpandible={viewModel.systemsExpandible}
								systems={viewModel.systemLandscapeSection.SYSTEMS}
								onSetAllLicensesFlag={viewModel.onSetAllLicensesFlag}
								totalSystemsCount={
									viewModel.systemLandscapeSection?.SYSTEM_LANDSCAPE_BUNDLES?.reduce(
										(count, bundle) => count + (bundle.SYSTEMS?.length ?? 0),
										0
									) ?? 0
								}
								totalLandscapesCount={
									viewModel.systemLandscapeSection?.SYSTEM_LANDSCAPE_BUNDLES
										?.length ?? 0
								}
								totalProductSystemsCount={viewModel.totalProductSystemsCount}
								productionSystemsCount={viewModel.productionSystemsCount}
								LANGUAGE_COUNT={viewModel.systemLandscapeSection.LANGUAGE_COUNT}
								enableSystemLandscapeCopyPaste={
									enableSystemLandscapeCopyPaste
								}
								onEnableSystemLandscapeCopyPaste={onEnableCopyPaste}
								selectedSystemsCount={selectedSystemKeys.size}
								clipboardBatches={clipboardBatches}
								onCopySelectedSystems={onCopySelectedSystems}
							/>
						</div>
						{viewModel
							.filterSystemLandscapeBundles()
							.map((systemLandscapeBundle) => {
								return (
									<SystemLandscape
										onSystemSaveDone={() => {
											viewModel.loadSystemLandscapeSection();
										}}
										key={systemLandscapeBundle.SYS_LANDSCAPE.OBJECT_ID}
										collapseSignal={viewModel.collapseSignal}
										onCollapseSingleSystem={viewModel.onCollapseSingleSystem}
										onExpandSingleSystem={viewModel.onExpandSingleSystem}
										contract={
											checkNotNull(viewModel.systemLandscapeSection).CONTRACT
										}
										expandSignal={viewModel.expandSignal}
										isLandscapeBeingDeleted={
											systemLandscapeBundle.SYS_LANDSCAPE ===
											viewModel.systemLandscapeUnderDeletion
										}
										lastProductVersion={
											checkNotNull(viewModel.systemLandscapeSection)
												.LAST_PRODUCT_VERSION
										}
										onSetAlternativeDownloadVersion={
											viewModel.onSetAlternativeDownloadVersion
										}
										product={
											checkNotNull(viewModel.systemLandscapeSection).PRODUCT
										}
										searchSignal={props.searchSignal}
										systemLandscape={systemLandscapeBundle.SYS_LANDSCAPE}
										systems={systemLandscapeBundle.SYSTEMS}
										allSystemsInProduct={allSystemsInProduct}
										systemsDraftCountsBySystemKey={
											systemsDraftCountsBySystemKey
										}
										onDraftCountsChange={onDraftCountsChange}
										systemLandscapeConflicts={systemLandscapeBundle.CONFLICTS}
										systemRequirementTypes={
											systemLandscapeBundle.SYS_LANDSCAPE
												.SYSTEM_REQUIREMENT_TYPES
										}
										systemRoles={
											systemLandscapeBundle.SYS_LANDSCAPE.SYSTEM_ROLES
										}
										onSave={viewModel.onSave}
										onDelete={viewModel.onDelete}
										enableSystemLandscapeCopyPaste={
											enableSystemLandscapeCopyPaste
										}
										clipboardPasteSystemsCount={
											clipboardPasteSystemsCount
										}
										onSystemDeletedForClipboardCleanup={
											onSystemDeletedForClipboardCleanup
										}
										onPasteClipboardSystems={onPasteClipboardSystems}
										isSystemSelected={isSystemSelected}
										onToggleSystemSelection={onToggleSystemSelection}
										onToggleAllSystemsInLandscape={
											onToggleAllSystemsInLandscape
										}
										pastedSystemsForLandscape={
											pastedDraftSystemsByLandscape[
												systemLandscapeBundle.SYS_LANDSCAPE.OBJECT_ID
											] ?? []
										}
									/>
								);
							})}
					</>
				)}
			</AsyncLoad>
			{!viewModel.isLoading && (
				<LoadingSpinnerButton
					showSpinner={viewModel.addSystemLandscape}
					icon='plus'
					text={t(texts.systemLandscape.labelAddButton)}
					onClick={viewModel.onAdd}
				/>
			)}
		</>
	);
};
